import {
  InferOutput,
  number,
  object,
  optional,
  pipe,
  safeParse,
  transform,
  ValiError,
} from 'valibot';

import { $getPlaylistsId } from './playlistsId.ts';
import {
  PlaylistItem,
  PlaylistItemSchema,
  PlaylistItemTypeWithTracks,
} from '../apiSchema.ts';
import {
  $getPlaylistIdTracks,
  PlaylistIdTracksData,
  PlaylistIdTracksRequestArgs,
  PlaylistIdTracksRequestArgsSchema,
} from './playlistIdTracks.ts';

export const PlaylistMediaPageRequestArgsSchema =
  PlaylistIdTracksRequestArgsSchema;

export const PlaylistMediaPageResponseSchema = pipe(
  object({
    playlist: PlaylistItemSchema,
    next: optional(number()),
    total: optional(number()),
  }),
  transform(({ playlist, next, total }) => {
    const { tracks } = playlist;
    const normalizePlaylist = {
      ...playlist,
      tracks: tracks ?? [],
    };
    return {
      playlist: normalizePlaylist,
      ...(next ? { next } : {}),
      total,
    };
  }),
);

export type PlaylistMediaPageRequestArgs = PlaylistIdTracksRequestArgs;

export type PlaylistMediaPageResponse = InferOutput<
  typeof PlaylistMediaPageResponseSchema
>;

/**
 * Fetch a specific playlist and its tracks by ID from the user's library.
 * @param {MusicKit.MusicKitInstance} player The MusicKit instance.
 * @param {PlaylistMediaPageRequestArgs} args The request arguments.
 * @returns {Promise<PlaylistMediaPageResponse} The playlist data.
 */
export const $getPlaylistMedia = async (
  player: MusicKit.MusicKitInstance,
  args: PlaylistMediaPageRequestArgs,
): Promise<PlaylistMediaPageResponse> => {
  const p = safeParse(
    PlaylistMediaPageRequestArgsSchema,
    args,
  );
  if (!p.success) {
    throw new ValiError(p.issues);
  }

  const { id, limit, offset, isLibrary } = p.output;

  const res = await Promise.all([
    $getPlaylistsId(player, { id, isLibrary }),
    $getPlaylistIdTracks(player, { id, limit, offset, isLibrary }),
  ]);

  const playlistWithoutTracks: PlaylistItem = res[0];
  const { tracks, next, total }: PlaylistIdTracksData = res[1];

  playlistWithoutTracks.tracks = tracks ?? [];
  const playlist = playlistWithoutTracks as PlaylistItemTypeWithTracks;

  return {
    playlist,
    next,
    total,
  };
};
